// Advanced Revenue Analytics and Data Processing
import { sanitizeMetric, getTimeBasedGoals } from "./goalCalculations";

// Enhanced data validation and sanitization
const validateRevenueData = (data) => {
  if (!data || !Array.isArray(data)) return [];

  return data
    .map((item) => ({
      ...item,
      revenue: sanitizeMetric(item.revenue, 0),
      downloads: sanitizeMetric(item.downloads, 0),
      subscriptions: sanitizeMetric(item.subscriptions, 0),
      downloadsRevenue: sanitizeMetric(item.downloadsRevenue, 0),
      subscriptionsRevenue: sanitizeMetric(item.subscriptionsRevenue, 0),
    }))
    .filter(
      (item) =>
        item.revenue >= 0 && item.downloads >= 0 && item.subscriptions >= 0
    );
};

// Advanced revenue calculations with NaN prevention
const calculateRevenueMetrics = (data, goals) => {
  const validatedData = validateRevenueData(data);

  if (validatedData.length === 0) {
    return {
      totalRevenue: 0,
      averageRevenue: 0,
      revenueGrowth: 0,
      revenueTrend: "stable",
      peakRevenue: 0,
      revenueVolatility: 0,
      conversionEfficiency: 0,
      revenuePerUser: 0,
      revenuePerDownload: 0,
      revenuePerSubscription: 0,
      isDataValid: false,
    };
  }

  const totalRevenue = validatedData.reduce(
    (sum, item) => sum + item.revenue,
    0
  );
  const totalDownloads = validatedData.reduce(
    (sum, item) => sum + item.downloads,
    0
  );
  const totalSubscriptions = validatedData.reduce(
    (sum, item) => sum + item.subscriptions,
    0
  );
  const averageRevenue = totalRevenue / validatedData.length;

  // Calculate revenue growth (comparing first half vs second half)
  const midPoint = Math.floor(validatedData.length / 2);
  const firstHalfRevenue = validatedData
    .slice(0, midPoint)
    .reduce((sum, item) => sum + item.revenue, 0);
  const secondHalfRevenue = validatedData
    .slice(midPoint)
    .reduce((sum, item) => sum + item.revenue, 0);

  const revenueGrowth =
    firstHalfRevenue > 0
      ? ((secondHalfRevenue - firstHalfRevenue) / firstHalfRevenue) * 100
      : 0;

  // Determine trend
  let revenueTrend = "stable";
  if (revenueGrowth > 5) revenueTrend = "increasing";
  else if (revenueGrowth < -5) revenueTrend = "decreasing";

  // Calculate peak revenue
  const peakRevenue = Math.max(...validatedData.map((item) => item.revenue));

  // Calculate revenue volatility (standard deviation)
  const mean = averageRevenue;
  const variance =
    validatedData.reduce(
      (sum, item) => sum + Math.pow(item.revenue - mean, 2),
      0
    ) / validatedData.length;
  const revenueVolatility = Math.sqrt(variance);

  // Calculate conversion efficiency (revenue per paywall view estimate)
  const estimatedPaywallViews = totalDownloads * 2.5; // Estimate based on conversion rates
  const conversionEfficiency =
    estimatedPaywallViews > 0 ? totalRevenue / estimatedPaywallViews : 0;

  // Calculate revenue per user metrics
  const totalUsers = totalDownloads + totalSubscriptions;
  const revenuePerUser = totalUsers > 0 ? totalRevenue / totalUsers : 0;
  const revenuePerDownload =
    totalDownloads > 0 ? totalRevenue / totalDownloads : 0;
  const revenuePerSubscription =
    totalSubscriptions > 0 ? totalRevenue / totalSubscriptions : 0;

  return {
    totalRevenue: sanitizeMetric(totalRevenue),
    averageRevenue: sanitizeMetric(averageRevenue),
    revenueGrowth: sanitizeMetric(revenueGrowth),
    revenueTrend,
    peakRevenue: sanitizeMetric(peakRevenue),
    revenueVolatility: sanitizeMetric(revenueVolatility),
    conversionEfficiency: sanitizeMetric(conversionEfficiency),
    revenuePerUser: sanitizeMetric(revenuePerUser),
    revenuePerDownload: sanitizeMetric(revenuePerDownload),
    revenuePerSubscription: sanitizeMetric(revenuePerSubscription),
    isDataValid: true,
    dataPoints: validatedData.length,
  };
};

// Advanced trend analysis
const analyzeRevenueTrends = (data, timeRange) => {
  const validatedData = validateRevenueData(data);

  if (validatedData.length < 3) {
    return {
      trend: "insufficient_data",
      confidence: 0,
      forecast: null,
      seasonality: null,
      anomalies: [],
    };
  }

  // Calculate moving averages
  const movingAverages = [];
  const windowSize = Math.min(7, Math.floor(validatedData.length / 3));

  for (let i = windowSize - 1; i < validatedData.length; i++) {
    const window = validatedData.slice(i - windowSize + 1, i + 1);
    const avg =
      window.reduce((sum, item) => sum + item.revenue, 0) / window.length;
    movingAverages.push({
      index: i,
      value: avg,
      date: validatedData[i].date,
    });
  }

  // Detect trend direction
  const firstHalf = movingAverages.slice(
    0,
    Math.floor(movingAverages.length / 2)
  );
  const secondHalf = movingAverages.slice(
    Math.floor(movingAverages.length / 2)
  );

  const firstHalfAvg =
    firstHalf.reduce((sum, item) => sum + item.value, 0) / firstHalf.length;
  const secondHalfAvg =
    secondHalf.reduce((sum, item) => sum + item.value, 0) / secondHalf.length;

  const trendSlope = secondHalfAvg - firstHalfAvg;
  const trendStrength = (Math.abs(trendSlope) / firstHalfAvg) * 100;

  let trend = "stable";
  if (trendSlope > 0 && trendStrength > 5) trend = "increasing";
  else if (trendSlope < 0 && trendStrength > 5) trend = "decreasing";

  // Calculate confidence based on data consistency
  const revenueValues = validatedData.map((item) => item.revenue);
  const mean =
    revenueValues.reduce((sum, val) => sum + val, 0) / revenueValues.length;
  const variance =
    revenueValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
    revenueValues.length;
  const stdDev = Math.sqrt(variance);
  const coefficientOfVariation = stdDev / mean;
  const confidence = Math.max(0, 100 - coefficientOfVariation * 100);

  // Simple forecasting (linear projection)
  const forecast =
    trend !== "stable"
      ? {
          nextPeriod: secondHalfAvg + trendSlope,
          confidence: Math.min(confidence, 85), // Cap confidence for forecasts
        }
      : null;

  // Detect anomalies (values > 2 standard deviations from mean)
  const anomalies = validatedData
    .map((item, index) => ({ ...item, index }))
    .filter((item) => Math.abs(item.revenue - mean) > 2 * stdDev);

  return {
    trend,
    confidence: sanitizeMetric(confidence),
    forecast,
    seasonality: null, // Could be enhanced with seasonal decomposition
    anomalies: anomalies.map((a) => ({
      date: a.date,
      value: a.revenue,
      deviation: a.revenue - mean,
    })),
    movingAverages,
  };
};

// Enhanced goal comparison with context
const getEnhancedGoalComparison = (
  current,
  previous,
  goal,
  metricType,
  context = {}
) => {
  const baseComparison = {
    current: sanitizeMetric(current),
    previous: sanitizeMetric(previous),
    goal: sanitizeMetric(goal),
    change: 0,
    progress: 0,
    isPositive: false,
    isOnTrack: false,
    metricType,
    confidence: 0,
    trend: "stable",
    insights: [],
  };

  // Calculate percentage change with enhanced logic
  if (previous > 0) {
    baseComparison.change = ((current - previous) / previous) * 100;
  } else if (goal > 0) {
    baseComparison.change = ((current - goal) / goal) * 100;
  }

  // Calculate progress towards goal
  if (goal > 0) {
    baseComparison.progress = Math.min((current / goal) * 100, 100);
  }

  baseComparison.isPositive = baseComparison.change >= 0;
  baseComparison.isOnTrack = current >= goal * 0.8;

  // Add confidence based on data quality
  if (current > 0 && goal > 0) {
    const variance = Math.abs(current - goal) / goal;
    baseComparison.confidence = Math.max(0, 100 - variance * 100);
  }

  // Determine trend
  if (baseComparison.change > 10) baseComparison.trend = "strong_increase";
  else if (baseComparison.change > 5) baseComparison.trend = "increase";
  else if (baseComparison.change < -10)
    baseComparison.trend = "strong_decrease";
  else if (baseComparison.change < -5) baseComparison.trend = "decrease";
  else baseComparison.trend = "stable";

  // Generate insights
  if (baseComparison.progress >= 100) {
    baseComparison.insights.push(
      "Goal exceeded! Consider setting higher targets."
    );
  } else if (baseComparison.progress >= 80) {
    baseComparison.insights.push(
      "On track to meet goal. Maintain current momentum."
    );
  } else if (baseComparison.progress >= 60) {
    baseComparison.insights.push(
      "Making good progress. Focus on acceleration strategies."
    );
  } else if (baseComparison.progress >= 40) {
    baseComparison.insights.push(
      "Moderate progress. Review strategies and optimize."
    );
  } else {
    baseComparison.insights.push(
      "Significant improvement needed. Consider strategy pivot."
    );
  }

  if (baseComparison.change > 20) {
    baseComparison.insights.push(
      "Exceptional growth! Investigate what's driving success."
    );
  } else if (baseComparison.change < -20) {
    baseComparison.insights.push(
      "Significant decline. Immediate action required."
    );
  }

  return baseComparison;
};

// Revenue forecasting
const forecastRevenue = (data, periods = 7) => {
  const validatedData = validateRevenueData(data);

  if (validatedData.length < 3) {
    return {
      forecast: [],
      confidence: 0,
      method: "insufficient_data",
    };
  }

  // Simple linear regression for forecasting
  const n = validatedData.length;
  const xValues = Array.from({ length: n }, (_, i) => i);
  const yValues = validatedData.map((item) => item.revenue);

  const sumX = xValues.reduce((sum, x) => sum + x, 0);
  const sumY = yValues.reduce((sum, y) => sum + y, 0);
  const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
  const sumXX = xValues.reduce((sum, x) => sum + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Generate forecast
  const forecast = [];
  for (let i = 0; i < periods; i++) {
    const predictedValue = slope * (n + i) + intercept;
    forecast.push({
      period: n + i + 1,
      predicted: Math.max(0, predictedValue),
      confidence: Math.max(50, 100 - i * 5), // Decreasing confidence over time
    });
  }

  // Calculate forecast confidence based on historical accuracy
  const historicalPredictions = xValues.map((x) => slope * x + intercept);
  const mse =
    yValues.reduce(
      (sum, actual, i) => sum + Math.pow(actual - historicalPredictions[i], 2),
      0
    ) / n;
  const rmse = Math.sqrt(mse);
  const avgRevenue = sumY / n;
  const confidence = Math.max(0, 100 - (rmse / avgRevenue) * 100);

  return {
    forecast,
    confidence: sanitizeMetric(confidence),
    method: "linear_regression",
    slope: sanitizeMetric(slope),
    intercept: sanitizeMetric(intercept),
  };
};

// Revenue optimization insights
const generateRevenueInsights = (data, goals, metrics) => {
  const insights = [];

  if (!data || data.length === 0) {
    insights.push(
      "No revenue data available. Focus on generating initial sales."
    );
    return insights;
  }

  const { totalRevenue, averageRevenue, revenueGrowth, conversionEfficiency } =
    metrics;

  // Revenue performance insights
  if (totalRevenue < goals.revenue.total * 0.5) {
    insights.push(
      "Revenue significantly below target. Review pricing strategy and marketing efforts."
    );
  } else if (totalRevenue >= goals.revenue.total) {
    insights.push(
      "Revenue target achieved! Consider expanding to new markets or features."
    );
  }

  // Growth insights
  if (revenueGrowth > 20) {
    insights.push(
      "Strong revenue growth detected. Scale successful strategies."
    );
  } else if (revenueGrowth < -10) {
    insights.push(
      "Revenue declining. Investigate customer churn and market changes."
    );
  }

  // Conversion insights
  if (conversionEfficiency < 1) {
    insights.push(
      "Low conversion efficiency. Optimize paywall and pricing strategy."
    );
  } else if (conversionEfficiency > 3) {
    insights.push(
      "High conversion efficiency. Consider premium pricing tiers."
    );
  }

  // Data quality insights
  if (data.length < 7) {
    insights.push(
      "Limited historical data. Continue collecting data for better insights."
    );
  }

  return insights;
};

// Export all functions
export {
  validateRevenueData,
  calculateRevenueMetrics,
  analyzeRevenueTrends,
  getEnhancedGoalComparison,
  forecastRevenue,
  generateRevenueInsights,
};
